import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Badge } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import DemoBanner from '../components/DemoBanner';
import { useAuth } from '../context/AuthContext';
import BrandImage from '../components/BrandImage';
import '../App.css';
import { API_BASE } from '../utils/api';

const ACESSO_LABEL = { admin: 'Administrador', gerente: 'Gerente', colaborador: 'Colaborador' };
const ACESSO_COLOR = { admin: 'danger', gerente: 'warning', colaborador: 'secondary' };

const MinhaConta = () => {
  const { user, logout, login, isDemo, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [userData, setUserData] = useState({ nome: '', numero_telefone: '' });
  const [userId, setUserId] = useState(null);
  const [senhaTemporaria, setSenhaTemporaria] = useState(false);

  const [error, setError] = useState(location.state?.message || '');
  const [success, setSuccess] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingSenha, setLoadingSenha] = useState(false);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  };

  useEffect(() => {
    if (!user && !isDemo) {
      navigate('/login');
      return;
    }

    if (isDemo) {
      setUserId(999);
      setUserData({
        nome: user?.nome || 'Usuário Demo',
        numero_telefone: '(11) 9 9999-9999'
      });
      setSenhaTemporaria(false);
      return;
    }

    if (!token) {
      logout();
      navigate('/login');
      return;
    }

    fetch(`${API_BASE}/users/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.status === 401) {
          logout();
          navigate('/login');
          throw new Error('Sessão expirada.');
        }
        if (!res.ok) throw new Error('Erro ao carregar dados do usuário.');
        return res.json();
      })
      .then(data => {
        setUserId(data.id);
        setUserData({
          nome: data.nome,
          numero_telefone: data.numero_telefone ? formatPhoneNumber(data.numero_telefone) : ''
        });
        setSenhaTemporaria(data.senha_temporaria);
      })
      .catch(err => {
        setError(err.message);
      });
  }, [user, logout, navigate, isDemo, token]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (isDemo) {
      setSuccess('[DEMO] Informações salvas com sucesso (simulação).');
      return;
    }

    setLoadingInfo(true);
    try {
      const cleanPhone = userData.numero_telefone.replace(/\D/g, '');
      const targetId = userId ?? user?.id;
      const res = await fetch(`${API_BASE}/users/${targetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          nome: userData.nome,
          cargo: user?.cargo || 'colaborador',
          acesso: user?.acesso || 'colaborador',
          numero_telefone: cleanPhone
        })
      });
      if (res.status === 401) {
        logout();
        navigate('/login');
        throw new Error('Sessão expirada.');
      }
      if (!res.ok) throw new Error('Erro ao atualizar dados.');
      setSuccess('Dados atualizados com sucesso!');
    } catch (err) {
      setError('Erro ao atualizar: ' + err.message);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (isDemo) {
      setSuccess('[DEMO] Senha alterada com sucesso.');
      setNewPassword('');
      setConfirmPassword('');
      return;
    }

    setLoadingSenha(true);
    try {
      const targetId = userId ?? user?.id;
      const res = await fetch(`${API_BASE}/users/${targetId}/senha`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Erro ao alterar senha.');
      }

      if (data.token) {
        login(data.token);
      }

      setSuccess('Senha atualizada com sucesso! Você pode navegar para outras páginas agora.');
      setNewPassword('');
      setConfirmPassword('');
      setSenhaTemporaria(false);
    } catch (err) {
      setError('Erro ao alterar senha: ' + err.message);
    } finally {
      setLoadingSenha(false);
    }
  };

  return (
    <Container fluid className="main-content">
      {isDemo && <DemoBanner />}
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 className="fw-bold" style={{ color: 'var(--brand-secondary, #1D1D1B)' }}>
            <i className="bi bi-person-circle me-2" style={{ color: 'var(--brand-primary)' }}></i> Minha Conta
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end d-none d-md-block">
          <BrandImage
            envKey="REACT_APP_BANNER_CONTA"
            defaultAsset="/client_assets/finger_athos.png"
            alt="Identidade Visual Perfil"
            style={{ maxWidth: 150, height: 'auto' }}
          />
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="shadow-sm border-0">{error}</Alert>
      )}
      {success && (
        <Alert variant="success" className="shadow-sm border-0">{success}</Alert>
      )}
      {senhaTemporaria && !location.state?.message && (
        <Alert variant="warning" className="shadow-sm border-0">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Sua senha atual é temporária. Recomendamos alterá-la para manter sua conta segura.
        </Alert>
      )}

      {/* Card de perfil resumido */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 64, height: 64, backgroundColor: 'var(--brand-primary)', color: '#fff', fontSize: '1.75rem' }}
            >
              <i className="bi bi-person-fill"></i>
            </div>
            <div>
              <div className="fw-bold fs-5">{userData.nome || user?.nome || '—'}</div>
              <div className="text-muted small">{user?.cargo || '—'}</div>
              <Badge bg={ACESSO_COLOR[user?.acesso] || 'secondary'} className="mt-1">
                {ACESSO_LABEL[user?.acesso] || user?.acesso || '—'}
              </Badge>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Ajuste de Grid: Lado a Lado em telas médias e grandes */}
      <Row>
        {/* Coluna Esquerda: Informações Pessoais */}
        <Col xs={12} md={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4 d-flex flex-column">
              <h5 className="mb-4 text-muted fw-bold border-bottom pb-2">Informações Pessoais</h5>
              <Form onSubmit={handleUpdate} className="d-flex flex-column flex-grow-1">
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="nome" className="small fw-semibold">Nome Completo</Form.Label>
                  <Form.Control
                    id="nome"
                    type="text"
                    value={userData.nome}
                    onChange={(e) => setUserData({ ...userData, nome: e.target.value })}
                    autoComplete="name"
                    className="bg-light"
                  />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label htmlFor="numero_telefone" className="small fw-semibold">Número de Telefone</Form.Label>
                  <Form.Control
                    id="numero_telefone"
                    type="tel"
                    value={userData.numero_telefone}
                    onChange={(e) => setUserData({ ...userData, numero_telefone: formatPhoneNumber(e.target.value) })}
                    autoComplete="tel"
                    placeholder="(DDD) 9 XXXX-XXXX"
                    className="bg-light"
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 fw-bold border-0 py-2 mt-auto"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                  disabled={loadingInfo}
                >
                  {loadingInfo
                    ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                    : <><i className="bi bi-save me-2"></i> Salvar Informações</>}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Coluna Direita: Segurança */}
        <Col xs={12} md={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4 d-flex flex-column">
              <h5 className="mb-4 text-muted fw-bold border-bottom pb-2">Segurança</h5>
              <Form onSubmit={handlePasswordChange} className="d-flex flex-column flex-grow-1">
                <Form.Control
                  id="username_hidden"
                  type="text"
                  value={userData.nome}
                  hidden
                  readOnly
                  autoComplete="username"
                />
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="nova_senha" className="small fw-semibold">Nova Senha</Form.Label>
                  <Form.Control
                    id="nova_senha"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Digite a nova senha..."
                    className="bg-light"
                  />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label htmlFor="confirmar_senha" className="small fw-semibold">Confirmar Nova Senha</Form.Label>
                  <Form.Control
                    id="confirmar_senha"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Repita a nova senha..."
                    className={`bg-light ${confirmPassword && confirmPassword !== newPassword ? 'is-invalid' : confirmPassword && confirmPassword === newPassword ? 'is-valid' : ''}`}
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <div className="invalid-feedback">As senhas não coincidem.</div>
                  )}
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 fw-bold border-0 py-2 mt-auto"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                  disabled={!newPassword || newPassword !== confirmPassword || loadingSenha}
                >
                  {loadingSenha
                    ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                    : <><i className="bi bi-key me-2"></i> Alterar Senha</>}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MinhaConta;